/**
 * Invitation API Client
 *
 * @module api/invitations
 */
import client from "./client";

// Matches the backend invitationListDTO (services/api-gateway/routes/routes.go)
// returned by GET /api/v1/workspaces/{wid}/invitations. Field names and
// types are the canonical server-side form; the previous declaration used
// different names (invitee_email, sent_at, etc.) that never matched
// reality — surfaced by /test:prod-loop Stage B.14 when rendering the
// pending-invitations section of the Members page.
export interface Invitation {
  id: string;
  tenant_id: string;
  workspace_id: string;
  email: string;
  role_id: string;
  invited_by: string;
  status: "pending" | "sent" | "accepted" | "expired" | "revoked";
  expires_at: string;
  created_at: string;
}

export interface BulkInviteRequest {
  invitations: {
    email: string;
    role_id: string;
    /**
     * The Haladó starting value (DECISIONS.md D-TSG-22 point 3): ON only.
     * `true` makes accepting the invitation turn the invitee's advanced_mode
     * on, unless they have already set it. Omitted means nothing is written.
     */
    advanced_mode_start?: true;
  }[];
}

// "pending" is the honest default after /test:prod-loop B.14.8 — the
// backend no longer claims "sent" when it hasn't actually delivered an
// email. Legacy "sent" is still listed for backwards compat with any
// pre-fix staging builds.
export interface BulkInviteResult {
  email: string;
  status: "pending" | "sent" | "error";
  invitation_id?: string;
  token?: string;
  reason?: string;
}

export interface BulkInviteResponse {
  results: BulkInviteResult[];
}

export interface ListInvitationsResponse {
  invitations: Invitation[];
}

export interface ResendInvitationResponse {
  message: string;
  invitation_id: string;
  expires_at: string;
}


/**
 * Send bulk invitations to multiple emails
 */
export async function bulkInviteMembers(
  workspaceId: string,
  request: BulkInviteRequest,
): Promise<BulkInviteResponse> {
  const { data } = await client.post<BulkInviteResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/invitations/bulk`,
    request,
  );
  return data;
}

/**
 * List invitations for a workspace
 */
export async function listInvitations(
  workspaceId: string,
  status?: string,
): Promise<Invitation[]> {
  const { data } = await client.get<ListInvitationsResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/invitations`,
    { params: status ? { status } : undefined },
  );
  return data.invitations;
}

/**
 * Resend an invitation (generates new token and extends expiration)
 */
export async function resendInvitation(
  workspaceId: string,
  invitationId: string,
): Promise<ResendInvitationResponse> {
  const { data } = await client.post<ResendInvitationResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
  );
  return data;
}

/**
 * Revoke (delete) a pending invitation
 */
export async function revokeInvitation(
  workspaceId: string,
  invitationId: string,
): Promise<void> {
  await client.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}`,
  );
}

/**
 * Accept an invitation by token. Creates workspace membership for the
 * authenticated user.
 */
export interface AcceptInvitationResponse {
  workspace_id: string;
  role_id?: string;
  status: "accepted" | "already_member";
}

export async function acceptInvitation(
  token: string,
): Promise<AcceptInvitationResponse> {
  const { data } = await client.post<AcceptInvitationResponse>(
    `/invitations/${encodeURIComponent(token)}/accept`,
  );
  return data;
}
